import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollText, Filter, RefreshCw, Eye, ShieldCheck, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../lib/api';

const ACTION_TONE = (action) => {
  if (!action) return 'bg-stone-100 text-stone-700 border-stone-200';
  if (action.includes('bank_details_revealed') || action.includes('reveal'))
    return 'bg-rose-50 text-rose-800 border-rose-200';
  if (action.startsWith('refund.mark-refunded') || action.startsWith('refund.mark-eft-paid') || action.includes('paid'))
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (action.startsWith('refund.cancel') || action.includes('failed'))
    return 'bg-stone-100 text-stone-700 border-stone-200';
  if (action.startsWith('refund.'))
    return 'bg-violet-50 text-violet-800 border-violet-200';
  if (action.includes('bank_details_submitted'))
    return 'bg-orange-50 text-orange-800 border-orange-200';
  if (action.includes('proof'))
    return 'bg-blue-50 text-blue-800 border-blue-200';
  return 'bg-stone-100 text-stone-700 border-stone-200';
};

const Pill = ({ children, className = '' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide border ${className}`}>
    {children}
  </span>
);

const formatTs = (iso) => {
  if (!iso) return '—';
  return iso.slice(0, 19).replace('T', ' ');
};

const metaSummary = (meta) => {
  if (!meta || typeof meta !== 'object') return '—';
  const keys = Object.keys(meta);
  if (!keys.length) return '—';
  return keys.slice(0, 3).map(k => `${k}=${typeof meta[k] === 'object' ? JSON.stringify(meta[k]).slice(0, 30) : String(meta[k]).slice(0, 24)}`).join(' · ');
};

const AuditDetailDialog = ({ entry, onClose }) => {
  if (!entry) return null;
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="audit-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1F3D2B]">Audit entry</DialogTitle>
          <DialogDescription>
            <Pill className={ACTION_TONE(entry.action)}>{entry.action}</Pill>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-1 text-sm">
          <Row label="Timestamp" value={formatTs(entry.created_at)} />
          <Row label="Actor" value={entry.actor_email || entry.actor_id || '—'} />
          <Row label="Actor role" value={entry.actor_role || '—'} />
          <Row label="Entity type" value={entry.entity_type} />
          <Row label="Entity ID" value={entry.entity_id} mono />
          <Row label="IP address" value={entry.ip_address || '—'} mono />
          <Row label="Audit ID" value={entry.audit_id} mono />
        </div>

        <div className="mt-4 bg-stone-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Metadata (sanitised)</h3>
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-medium">
              <ShieldCheck className="w-3 h-3" /> Sensitive fields stripped
            </span>
          </div>
          {entry.metadata && Object.keys(entry.metadata).length > 0 ? (
            <pre className="text-xs text-stone-700 font-mono whitespace-pre-wrap break-words" data-testid="audit-metadata-block">
{JSON.stringify(entry.metadata, null, 2)}
            </pre>
          ) : (
            <div className="text-xs text-stone-500">No metadata recorded.</div>
          )}
        </div>

        {entry.entity_type === 'refund_request' && entry.entity_id && (
          <div className="mt-4">
            <a
              href={`/admin/refunds?focus=${entry.entity_id}`}
              className="text-sm text-[#1F3D2B] underline hover:no-underline"
              data-testid="audit-related-refund-link"
            >
              Open related refund →
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between text-xs py-1">
    <span className="text-stone-500">{label}</span>
    <span className={`${mono ? 'font-mono' : ''} text-stone-800 text-right break-all`}>{value || '—'}</span>
  </div>
);

const AdminAuditLogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ actions: [], entity_types: [], actor_roles: [] });
  const [selected, setSelected] = useState(null);

  const [fActorEmail, setFActorEmail] = useState('');
  const [fActorRole, setFActorRole] = useState('all');
  const [fAction, setFAction] = useState('all');
  const [fEntityType, setFEntityType] = useState('all');
  const [fEntityId, setFEntityId] = useState('');
  const [fIp, setFIp] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin only.');
      navigate('/');
    }
  }, [user, navigate]);

  const fetchFacets = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/audit-logs/_meta/facets`, { withCredentials: true });
      setFacets(r.data || { actions: [], entity_types: [], actor_roles: [] });
    } catch {
      /* ignore — filter dropdowns just show "all" */
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fActorEmail.trim()) params.actor_email = fActorEmail.trim().toLowerCase();
      if (fActorRole !== 'all') params.actor_role = fActorRole;
      if (fAction !== 'all') params.action = fAction;
      if (fEntityType !== 'all') params.entity_type = fEntityType;
      if (fEntityId.trim()) params.entity_id = fEntityId.trim();
      if (fIp.trim()) params.ip_address = fIp.trim();
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      const r = await axios.get(`${BACKEND_URL}/api/admin/audit-logs`, {
        withCredentials: true,
        params,
      });
      setRows(r.data.audit_logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchFacets();
      fetchRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openDetail = async (r) => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/admin/audit-logs/${r.audit_id}`, { withCredentials: true });
      setSelected(resp.data.audit_log);
    } catch {
      // Fall back to the row we already have
      setSelected(r);
    }
  };

  const exportCsv = async () => {
    // Build the same param set as fetchRows so the export honours active filters.
    const params = new URLSearchParams();
    if (fActorEmail.trim()) params.set('actor_email', fActorEmail.trim().toLowerCase());
    if (fActorRole !== 'all') params.set('actor_role', fActorRole);
    if (fAction !== 'all') params.set('action', fAction);
    if (fEntityType !== 'all') params.set('entity_type', fEntityType);
    if (fEntityId.trim()) params.set('entity_id', fEntityId.trim());
    if (fIp.trim()) params.set('ip_address', fIp.trim());
    if (fFrom) params.set('date_from', fFrom);
    if (fTo) params.set('date_to', fTo);

    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/audit-logs.csv?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Filename: prefer server's Content-Disposition, else build our own.
      const cd = r.headers['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `vinepass-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1F3D2B] flex items-center gap-3">
              <ScrollText className="w-8 h-8 text-[#9C7434]" /> Audit Log
            </h1>
            <p className="text-sm text-stone-600 mt-1">
              Read-only record of sensitive admin actions. All metadata is sanitised — full banking details are never shown here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchRows} data-testid="audit-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90 text-white"
              data-testid="audit-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Export current filtered view
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-stone-200 p-4 mb-6">
          <div className="flex items-center gap-2 text-xs text-stone-600 mb-3">
            <Filter className="w-3.5 h-3.5" />
            <span className="font-medium uppercase tracking-wider">Filters</span>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Actor email</Label>
              <Input data-testid="audit-filter-actor-email" value={fActorEmail} onChange={(e) => setFActorEmail(e.target.value)} placeholder="admin@vinepass.co.za" />
            </div>
            <div>
              <Label className="text-xs">Actor role</Label>
              <Select value={fActorRole} onValueChange={setFActorRole}>
                <SelectTrigger data-testid="audit-filter-actor-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {facets.actor_roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={fAction} onValueChange={setFAction}>
                <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {facets.actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Entity type</Label>
              <Select value={fEntityType} onValueChange={setFEntityType}>
                <SelectTrigger data-testid="audit-filter-entity-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {facets.entity_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Entity ID</Label>
              <Input data-testid="audit-filter-entity-id" value={fEntityId} onChange={(e) => setFEntityId(e.target.value)} placeholder="UUID" />
            </div>
            <div>
              <Label className="text-xs">IP address</Label>
              <Input data-testid="audit-filter-ip" value={fIp} onChange={(e) => setFIp(e.target.value)} placeholder="x.x.x.x" />
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input data-testid="audit-filter-from" value={fFrom} onChange={(e) => setFFrom(e.target.value)} type="date" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input data-testid="audit-filter-to" value={fTo} onChange={(e) => setFTo(e.target.value)} type="date" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={fetchRows} className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90" data-testid="audit-filter-apply-btn">
              Apply filters
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-stone-500">Loading audit log…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 p-12 text-center" data-testid="audit-empty-state">
            <ScrollText className="w-10 h-10 text-stone-400 mx-auto mb-3" />
            <p className="text-stone-600">No audit entries match these filters.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden" data-testid="audit-list">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr className="text-left">
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">When</th>
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actor</th>
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Action</th>
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden md:table-cell">Entity</th>
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden lg:table-cell">Metadata</th>
                    <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden lg:table-cell">IP</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.audit_id} className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`audit-row-${(r.audit_id || '').slice(0, 8)}`}>
                      <td className="p-3 text-xs text-stone-600 font-mono">{formatTs(r.created_at)}</td>
                      <td className="p-3">
                        <div className="text-stone-800 text-sm">{r.actor_email || '—'}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-wider">{r.actor_role || '—'}</div>
                      </td>
                      <td className="p-3"><Pill className={ACTION_TONE(r.action)}>{r.action}</Pill></td>
                      <td className="p-3 hidden md:table-cell text-xs text-stone-700">
                        <div className="font-medium">{r.entity_type || '—'}</div>
                        <div className="font-mono text-[10px] text-stone-500 truncate max-w-[180px]">{r.entity_id ? r.entity_id.slice(0, 8).toUpperCase() : '—'}</div>
                      </td>
                      <td className="p-3 hidden lg:table-cell text-xs text-stone-600 max-w-[280px] truncate">{metaSummary(r.metadata)}</td>
                      <td className="p-3 hidden lg:table-cell text-xs text-stone-600 font-mono">{r.ip_address || '—'}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`audit-open-${(r.audit_id || '').slice(0, 8)}`}>
                          <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-xs text-stone-500 text-right">Showing {rows.length} entries (max 500).</div>
          </>
        )}
      </div>

      <AuditDetailDialog entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default AdminAuditLogPage;
